/**
 * Bascule de thème — clair / sombre / système, mémorisée par utilisateur (U6b vague 10).
 *
 * Planche MenuUtilisateurTelephone : « Affichage › Thème » est un groupe de trois
 * `menuitemradio` posé directement dans le menu « Compte et préférences » (plus de
 * sous-menu : au doigt, un sous-menu latéral déborde de l'écran de 390 px). Monté dans
 * `DisplayMenuContent`, donc toujours sous un `DropdownMenuContent` (`role="menu"`), dont
 * `menuitemradio` dans un `group` est un enfant valide (aria-required-children).
 * Le menu reste ouvert après le choix : l'effet se voit aussitôt, la coche suit.
 */
import { useState, type JSX } from 'react'
import { Moon, Sun, Monitor } from 'lucide-react'

import { DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@/components/ui/dropdown-menu'
import { useSession } from '@/hooks/useSession'
import { useT } from '@/i18n/language'
import { getTheme, setTheme, type Theme } from '@/lib/theme'

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Clair', icon: Sun },
  { value: 'dark', label: 'Sombre', icon: Moon },
  { value: 'system', label: 'Système', icon: Monitor },
]

export function ThemeToggle(): JSX.Element {
  const { t } = useT()
  const utilisateur = useSession()?.username || undefined
  const [theme, setThemeState] = useState<Theme>(() => getTheme(utilisateur))

  const choose = (nouveau: string): void => {
    const valeur = OPTIONS.find((o) => o.value === nouveau)?.value ?? 'system'
    setTheme(valeur, utilisateur)
    setThemeState(valeur)
  }

  const idLibelle = 'menu-theme-libelle'
  return (
    <>
      <DropdownMenuLabel id={idLibelle} className="text-xs font-medium text-muted-foreground">
        {t('display.theme', 'Thème')}
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup value={theme} onValueChange={choose} data-testid="theme-toggle"
                              aria-labelledby={idLibelle}>
        {OPTIONS.map((opt) => (
          <DropdownMenuRadioItem key={opt.value} value={opt.value} data-testid={`theme-${opt.value}`}
                                 onSelect={(e) => e.preventDefault()} className="min-h-10">
            <opt.icon className="size-4" aria-hidden="true" />
            <span>{t(`theme.${opt.value}`, opt.label)}</span>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  )
}
